import { useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { PAYROLL_ENABLED, PAYROLL_PERMISSION } from '../../config/payrollFeature';
import { useMyOnboarding } from '../../hooks/useQueries';
import usePendingApprovals from '../../hooks/usePendingApprovals';
import { useMySurveys } from '../../hooks/useSurveys';
import { countSurveysWaiting } from '../../lib/surveys';

// Onboarding is a page that exists only while somebody is going through it,
// so the link to it comes and goes with their own progress.
function useOnboardingLink() {
  const onboarding = useMyOnboarding({ enabled: true });
  const isOnboarding = onboarding.data?.assigned === true && onboarding.data?.status !== 'completed';

  // Steps still to work through. A journey with none configured yet is not
  // something to nag about, so the dot follows what is actually outstanding
  // rather than the presence of the link.
  const remaining = isOnboarding
    ? Math.max((onboarding.data.total_steps || 0) - (onboarding.data.completed_count || 0), 0)
    : 0;

  return { isOnboarding, remaining };
}

// The sidebar's links: everyone's first, then the "Admin Only" section. The
// package drops the divider when no admin link follows it.
export default function useNavItems() {
  const auth = useAuth();
  const { isOnboarding, remaining } = useOnboardingLink();

  // The same count the home page badges and the inbox itself shows, so the dot
  // can never disagree with the queue it points at. Both queries behind it are
  // permission-gated and cached, so this costs a non-approver nothing.
  const { pendingTotal } = usePendingApprovals();

  // Open surveys sent to them that they haven't sent back yet.
  const mySurveys = useMySurveys();
  const surveysWaiting = countSurveysWaiting(mySurveys.data);

  return useMemo(() => {
    const hasAny = (permissions) => permissions.some((permission) => auth.hasPermission(permission));
    const canApproveTimeOff = auth.isApprover() || auth.isAdmin() || auth.hasPermission('approve_time_off');
    const canApproveExpenses = auth.isFinance() || auth.isAdmin() || auth.hasPermission('approve_expenses');
    const canSeeApprovals = canApproveTimeOff || canApproveExpenses || auth.hasPermission('view_all_expenses');

    return [
      { path: '/', icon: 'bi-house', label: 'Home' },
      isOnboarding && {
        path: '/onboarding',
        icon: 'bi-signpost-2',
        label: 'Onboarding',
        dot: remaining > 0,
        dotLabel: `${remaining} step${remaining === 1 ? '' : 's'} still to do`,
      },
      { path: '/me', icon: 'bi-person', label: 'Me' },
      {
        path: '/surveys',
        icon: 'bi-ui-checks',
        label: 'Surveys',
        dot: surveysWaiting > 0,
        dotLabel: `${surveysWaiting} survey${surveysWaiting === 1 ? '' : 's'} waiting on you`,
      },
      { path: '/time-off', icon: 'bi-calendar-event', label: 'Time Off' },
      { path: '/expenses', icon: 'bi-receipt', label: 'Expenses' },
      { path: '/view-hours', icon: 'bi-clock-history', label: 'Timesheet' },

      { divider: true, label: 'Admin Only' },
      hasAny(['manage_all_employees', 'manage_coached_employees', 'manage_roles', 'manage_hiring', 'manage_resumes', 'manage_payroll', 'create_surveys'])
        && { path: '/admin', icon: 'bi-shield-lock', label: 'Admin' },
      hasAny(['view_timecamp_breakdown'])
        && { path: '/reports/timecamp', icon: 'bi-bar-chart', label: 'TimeCamp Report' },
      // Company-specific payroll module. The flag hides it wholesale; the
      // permission gates it for everyone else. Removing the module means
      // deleting this entry and the import above.
      PAYROLL_ENABLED && hasAny([PAYROLL_PERMISSION])
        && { path: '/admin/payroll', icon: 'bi-cash-stack', label: 'Payroll' },
      canSeeApprovals && {
        path: '/approvals',
        icon: 'bi-check-circle',
        label: 'Approvals',
        dot: pendingTotal > 0,
        dotLabel: `${pendingTotal} waiting on you`,
      },
      // The EmployeeConnect timesheet, admin-only while it is in preview. '*' is
      // the administrator grant, so no narrower permission reveals it.
      hasAny(['*']) && { path: '/timesheet', icon: 'bi-stopwatch', label: 'Timesheet (Preview)' },
    ];
  }, [auth, isOnboarding, remaining, pendingTotal, surveysWaiting]);
}

// The phone tab bar keeps its own shorter list: the sections someone reaches
// for on a phone, in the order the bar has always shown them.
export function useBottomNavItems() {
  const auth = useAuth();
  const { isOnboarding } = useOnboardingLink();

  return useMemo(() => {
    const canAccessAdmin =
      auth.hasPermission('manage_all_employees')
      || auth.hasPermission('manage_coached_employees')
      || auth.hasPermission('manage_roles')
      || auth.hasPermission('manage_hiring')
      || auth.hasPermission('manage_resumes')
      || auth.hasPermission('create_surveys');
    const canSeeApprovals = auth.isFinance() || auth.isApprover() || auth.hasPermission('view_all_expenses');

    return [
      { path: '/me', icon: 'bi-person', label: 'Me' },
      isOnboarding && { path: '/onboarding', icon: 'bi-signpost-2', label: 'Onboarding' },
      canAccessAdmin && { path: '/admin', icon: 'bi-shield-lock', label: 'Admin' },
      canSeeApprovals && { path: '/approvals', icon: 'bi-check-circle', label: 'Approvals' },
      { path: '/time-off', icon: 'bi-calendar-event', label: 'Time Off' },
      { path: '/view-hours', icon: 'bi-clock-history', label: 'Hours' },
    ];
  }, [auth, isOnboarding]);
}
